import { FC, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { Box,Tooltip, useTheme } from '@mui/material'
import { AppDispatch, RootState } from 'redux/store'
import { setAcademicYear, setGradeLevel, setGroup, setGroupCourse, setStudent, } from 'redux/ui/groupCourseGradebookUI'
import { useTranslateGrade } from 'utils/customHooks/useTranslateGrade'
import { useGET_gradebookGridStudentQuery } from 'redux/api/gradebookApi'
import { ArrowCircleDownOutlined, ArrowCircleRightOutlined, Check } from '@mui/icons-material'
import StudentGradebookFilters from 'components/organisms/forms/tableFilters/students/StudentGradebookFilters'
import React from 'react'

interface IProps {
  student?: string
  viewOnly?: boolean
  tabText?: string
  tabKey?: string
}

const StudentGradebook: FC<IProps> = ({ student='', viewOnly=false }) => {
  const { t } = useTranslation()
  const [translateGrade, gradeColor] = useTranslateGrade();
  const dispatch = useDispatch<AppDispatch>()

  const [openRow, setOpenRow] = useState(null)

  const reduxKey = 'groupCourseGradebook';

  const academic_year =  useSelector((state: RootState) => state[reduxKey].tableFilters.academic_year)
  const group_course =  useSelector((state: RootState) => state[reduxKey].tableFilters.group_course)
  const ui_student =  useSelector((state: RootState) => state[reduxKey].tableFilters.student)

  const selectedStudent = student || ui_student
  
  const { data } = useGET_gradebookGridStudentQuery({
    student_id: selectedStudent || '',
    academic_year: academic_year || '',
    params: {
      group_course: group_course || ''
    }
  }, {skip: !academic_year || !selectedStudent})


  useEffect(() => {
    dispatch(setAcademicYear(null))
    dispatch(setGradeLevel(null))
    dispatch(setGroup(null))
    dispatch(setStudent(null))
    dispatch(setGroupCourse(null))
  }, [])


  return (
    <>
      <StudentGradebookFilters student={student}/>  

      {data && <div style={{ marginTop: '40px' }}>
        
        <div className='gradebook-grid-wrapper'>
          <table className='gradebook-grid gradebook-grid-full' style={{ minWidth: `${ 590+data.academic_periods.length*550 }px` }}>
            <tbody>
            <tr>
              <td style={{ width: '200px', textAlign: 'center', fontWeight: '600', fontSize: '14px' }}>
                {t('common.courses')}
              </td>
              <td style={{ width: '100px', textAlign: 'center' }}>
                {t('common.accommodations-short')}
              </td>
              <td style={{ width: '100px', textAlign: 'center' }}>
                {t('common.modifications-short')}
              </td>
              <td style={{ position: 'relative', width: '100px', fontWeight: '600', fontSize: '14px', textAlign: 'center' }} className='gradebook-grid-orange'>
                <Tooltip title={t('common.real-time-calculation')} placement='top'>
                    <div>
                      {t('common.overall')}
                      <div className="gradebook-grid-hint">{t('common.calculated')}*</div>
                    </div>
                </Tooltip>
              </td>
              <td className='gradebook-grid-dark-blue'
                style={{ width: '80px', textTransform: 'uppercase', textAlign: 'center', fontWeight: '600', fontSize: '14px' }}>
                {t('common.final-grade')}
              </td>
              {data.academic_periods.map(term => <React.Fragment key={`h-${term.id}`}>
              <td colSpan={2} style={{ width: '350px', paddingLeft: '20px', fontSize: '16px', fontWeight: '600' }} 
                   className='gradebook-grid-blue'>
                  {term.title}
              </td>
              <td style={{ width: '200px', fontWeight: '600', fontSize: '12px' }} 
                className='gradebook-grid-blue'>
                  <div style={{ fontSize: '12px', lineHeight: '14px'}}>{term.title}<br />{t('common.final-grade')}</div>
              </td>
              <td rowSpan={2+data.group_courses.length+20} style={{width:'4px',padding: '0px'}}></td>
              </React.Fragment>)}
            </tr>

            <tr>
              <td></td>
              <td></td>
              <td></td>
              <td className='gradebook-grid-orange'></td>
              <td style={{ width: '80px', fontSize: '10px', textAlign: 'center' }} className='gradebook-grid-dark-blue'></td>
              {data.academic_periods.map(term => <React.Fragment key={`s-${term.id}`}>
                <td style={{ width: '80px', fontSize: '10px' }} className='gradebook-grid-light-blue'>
                  <Tooltip title={t('common.pre-assessment-grade')} placement='top'>
                   <span>{t('common.entry-level')}</span>
                  </Tooltip>
                </td>
                <td style={{ width: '250px', fontSize: '10px' }} className='gradebook-grid-light-blue'>{t('common.current')}</td>
                <td style={{ width: '80px', fontSize: '10px' }} className='gradebook-grid-light-blue'></td>
              </React.Fragment>)}
            </tr>

            {data.group_courses.map( (gc, gcIdx) => <React.Fragment key={`${gc.id}`}>
              <tr key={gc.id} className={`gradebook-main-row  ${gcIdx%2==0 ? 'odd' : 'even'}`}>
                <td style={{ paddingTop: '8px', paddingBottom: '8px' }}>
                  
                  <div 
                    onClick={() => {
                      if(openRow == gc.id)
                        setOpenRow(null)
                      else
                        setOpenRow(gc.id)
                    }}
                    style={{ display: 'flex', gap: '5px', alignItems: 'center', cursor: 'pointer' }}>
                    {openRow == gc.id 
                      ? <ArrowCircleDownOutlined sx={{ color: '#63b9ff' }} /> 
                      : <ArrowCircleRightOutlined sx={{ color: '#63b9ff' }} /> }
                    <span>{gc.title}</span>
                  </div>
                </td>
                <td>
                  {gc.accommodations && gc.accommodations.length > 0 && <div style={{ textAlign: 'center'}}>
                    <Tooltip title={gc.accommodations.join(', ')} placement='top'><Check /></Tooltip>
                  </div>}
                </td>
                <td>
                  {gc.modifications.length > 0 && <div style={{ textAlign: 'center'}}>
                    <Tooltip title={gc.modifications.join(', ')} placement='top'><Check /></Tooltip>
                  </div>}
                </td>

                <td style={{ textAlign: 'center' }}>
                  {gc.calculated_grade_percentage ? `${gc.calculated_grade_percentage}%` : ''}
                </td>
                <td className='gradebook-grid-dark-blue'  style={{ textAlign: 'center' }}>
                  {gc.final_grade_percentage ? 
                      <span className="grading-circle" 
                        style={{ background: gradeColor(gc.final_grade_percentage) }}>
                          {translateGrade(gc.final_grade_percentage)}
                      </span>
                       : ''}
                </td>

                {data.academic_periods.map(term => { 
                  
                  const academicPeriod = gc.academic_periods?.find(ap => ap.id == term.id) ?? null
                  
                  return <React.Fragment key={`${term.id}`}>
                  <td>
                      <div style={{ display: 'flex', gap: '10px', fontSize: '16px', paddingLeft: '10px' }}>
                      {academicPeriod && academicPeriod.entry_assignments.length > 0  && 
                        academicPeriod.entry_assignments.map(a => <Tooltip key={a.assessment_id} 
                          title={a.assignment_title} placement='top'>
                            <span>{translateGrade(a.grade_percentage)}</span>
                        </Tooltip>)}
                      </div>
                  </td>
                  <td>
                     <div style={{ display: 'flex', gap: '10px', fontSize: '16px' }}>
                      {academicPeriod && academicPeriod.regular_assignments.length > 0  && 
                        academicPeriod.regular_assignments.map(a => <Tooltip key={a.assessment_id} 
                          title={a.assignment_title} placement='top'>
                            <span>{translateGrade(a.grade_percentage)}</span>
                        </Tooltip>)}
                      </div>
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    {academicPeriod.final_grade_percentage ? 
                      <span className="grading-circle" 
                        style={{ background: gradeColor(academicPeriod.final_grade_percentage) }}>
                          {translateGrade(academicPeriod.final_grade_percentage)}
                      </span>
                       : ''}
                  </td>
                </React.Fragment>})}
              </tr>

              {openRow == gc.id && gc.components.map( (c,idx) => {
                
                return <tr key={`sr-${gc.id}-${c.id}`} className={`gradebook-grid-subrow ${idx%2==0 ? 'odd' : 'even'}`}>
                  <td colSpan={3}>{c.title}</td>
                  <td style={{textAlign: 'center'}}>{c.importance}</td>
                  <td></td>

                  {
                      data.academic_periods.map(term => { 
                    
                        const academicPeriod = gc.academic_periods?.find(ap => ap.id == term.id) ?? null
                      
                        return <React.Fragment  key={`sr-${gc.id}-${c.id}-${term.id}`}>
                          <td>
                              <div style={{ display: 'flex', gap: '10px', fontSize: '16px', paddingLeft: '10px' }}>
                              {academicPeriod && academicPeriod.entry_assignments.length > 0  && 
                                academicPeriod.entry_assignments.filter(a => a.component_id == c.id).map(a => 
                                <Tooltip key={a.assessment_id} 
                                  title={a.assignment_title} placement='top'>
                                    <span>{translateGrade(a.grade_percentage)}</span>
                                </Tooltip>)}
                              </div>
                          </td>
                          <td>
                            <div style={{ display: 'flex', gap: '10px', fontSize: '16px' }}>
                              {academicPeriod && academicPeriod.regular_assignments.length > 0  && 
                                academicPeriod.regular_assignments.filter(a => a.component_id == c.id).map(a => 
                                <Tooltip key={a.assessment_id} 
                                  title={a.assignment_title} placement='top'>
                                    <span>{translateGrade(a.grade_percentage)}</span>
                                </Tooltip>)}
                              </div>
                          </td>
                          <td></td>
                          </React.Fragment>
                      }) 
                    }

                  <td></td>
              </tr>})}

              </React.Fragment>         
            )}
            </tbody>
          </table>
        </div>        

      </div>}

    </>
  )
}
export default StudentGradebook
